/* eslint-disable import/no-cycle */
import { defineMessages } from "react-intl"
import AssessmentIcon from "@material-ui/icons/Assessment"
import ListAltIcon from "@material-ui/icons/ListAlt"
import TrendingUpIcon from "@material-ui/icons/TrendingUp"
import AccountTreeIcon from "@material-ui/icons/AccountTree"
import BarChartIcon from "@material-ui/icons/BarChart"
import MergeTypeIcon from "@material-ui/icons/MergeType"
import {
  DateWithPortfolios,
  MonthsPortfoliosSelector,
  PortfoliosSelector,
  YearPortfolioSelector,
  YearSelector
} from "./Selectors"
import withWidgets from "../../component/widgets/withWidgets"
import {
  WEALTH_PORTFOLIO_SELECTOR_ID,
  WEALTH_CUSTOMER_SELECTOR_ID,
  WEALTH_YEAR_SELECTOR_ID
} from "../../api/widgetSlice"
import { WIDGET_TYPES } from "../../widgets/types"
import TemplateWealthPage from "./TemplateWealthPage"
import SynthesisCustomTab from "./SynthesisCustomTab"
import adminMessage from "../admin/adminMessage"
import PerformanceCustomTab from "./PerformanceCustomTab"

/**
 * Available tabs in wealth pages.
 * @type {string[]}
 */
export const synthesisPageID = "wealth.synthesisTab"
export const movementPageID = "wealth.movementTab"
export const performancePageID = "wealth.performanceTab"
export const compositionPageID = "wealth.compositionTab"
export const simulationsPageID = "wealth.simulationsTab"
export const consolidationPageId = "wealth.consolidationTab"

/**
 * Enum of static wealth pages of standard webportal.
 * See: webportal-model > lib > models > page.js
 */
export const PAGE_IDENTIFIER = {
  overview: "overview",
  movements: "movements",
  performance: "performance",
  composition: "composition",
  simulations: "simulations",
  consolidation: "consolidation"
}

export const messages = defineMessages({
  wealthComposition: {
    id: compositionPageID,
    defaultMessage: "Wealth > Composition"
  },
  wealthConsolidation: {
    id: consolidationPageId,
    defaultMessage: "Wealth > Consolidation"
  },
  wealthMovements: {
    id: movementPageID,
    defaultMessage: "Wealth > Movements"
  },
  wealthPerformance: {
    id: performancePageID,
    defaultMessage: "Wealth > Performance"
  },
  wealthSynthesis: {
    id: synthesisPageID,
    defaultMessage: "Wealth > Synthesis"
  },
  wealthSimulations: {
    id: simulationsPageID,
    defaultMessage: "Wealth > Simulations"
  },
  title: {
    defaultMessage: "Wealth",
    id: "menu.wealth"
  },
  synthesis: {
    defaultMessage: "Synthesis",
    id: "menu.wealth.synthesis"
  },
  movement: {
    defaultMessage: "Movement",
    id: "menu.wealth.movement"
  },
  performance: {
    defaultMessage: "Performance",
    id: "menu.wealth.performance"
  },
  composition: {
    defaultMessage: "Composition",
    id: "menu.wealth.composition"
  },
  consolidation: {
    defaultMessage: "Consolidation",
    id: "menu.wealth.consolidation"
  },
  simulations: {
    defaultMessage: "Simulations",
    id: "menu.wealth.simulations"
  },
  compositionSubtitle: {
    defaultMessage: "Composition of your portfolios",
    id: "private.composition.subtitle"
  },
  movementsSubtitle: {
    defaultMessage: "Last trades",
    id: "private.trades.subtitle"
  },
  performancesSubtitle: {
    defaultMessage: "Evolution of your portfolios",
    id: "private.performance.subtitle"
  },
  synthesisSubtitle: {
    defaultMessage: "Summary of your portfolios",
    id: "private.synthesis.subtitle"
  },
  simulationsSubtitle: {
    defaultMessage: "Allocation Proposal",
    id: "private.simulations.subtitle"
  }
})

/**
 * Template widget page static props
 */
const compositionTabProps = {
  widgetPage: compositionPageID,
  selectorReducerName: WEALTH_PORTFOLIO_SELECTOR_ID,
  subtitle: messages.compositionSubtitle
}

const consolidationTabProps = {
  widgetPage: consolidationPageId,
  selectorReducerName: WEALTH_YEAR_SELECTOR_ID,
  subtitle: messages.consolidation
}

const movementTabProps = {
  widgetPage: movementPageID,
  selectorReducerName: WEALTH_PORTFOLIO_SELECTOR_ID,
  subtitle: messages.movementsSubtitle
}

const performanceTabProps = {
  widgetPage: performancePageID,
  selectorReducerName: WEALTH_PORTFOLIO_SELECTOR_ID,
  subtitle: messages.performancesSubtitle
}

const synthesisTabProps = {
  widgetPage: synthesisPageID,
  selectorReducerName: WEALTH_CUSTOMER_SELECTOR_ID,
  subtitle: messages.synthesisSubtitle
}

const simulationsTabProps = {
  widgetPage: simulationsPageID,
  selectorReducerName: WEALTH_PORTFOLIO_SELECTOR_ID,
  subtitle: messages.simulationsSubtitle
}

/**
 * A DSL configuration for template page rendering.
 *
 * It binds a template from to a set of Widgets component.
 *
 * See package src/pages/wealth/Tabs
 * See package src/pages/assetmanagement/Tabs
 */
const pagesDSL = {
  wealth: {
    synthesisTab: [
      {
        page: synthesisPageID,
        type: WIDGET_TYPES.DASHBOARD,
        alias: "wealth_synthesis_dashboard_0",
        subscriptions: { customer_list: "wealth_customer_selector" }
      }
    ],
    movementTab: [
      {
        page: movementPageID,
        type: WIDGET_TYPES.DASHBOARD,
        alias: "wealth_movement_dashboard_0",
        subscriptions: {
          asset_list: "wealth_portfolio_selector",
          end_date_list: "wealth_end_month_picker"
        }
      },
      {
        page: movementPageID,
        type: WIDGET_TYPES.DASHBOARD,
        alias: "wealth_movement_dashboard_1",
        subscriptions: {
          asset_list: "wealth_portfolio_selector"
        }
      }
    ],
    performanceTab: [
      {
        page: performancePageID,
        type: WIDGET_TYPES.DASHBOARD,
        alias: "wealth_performance_dashboard_0",
        subscriptions: {
          asset_list: "wealth_portfolio_selector",
          end_date_list: "wealth_end_date_picker"
        }
      }
    ],
    compositionTab: [
      {
        page: compositionPageID,
        type: WIDGET_TYPES.DASHBOARD,
        alias: "wealth_composition_dashboard_0",
        subscriptions: {
          asset_list: "wealth_portfolio_selector",
          end_date_list: "wealth_end_date_picker"
        }
      }
    ],
    consolidationTab: [
      {
        page: consolidationPageId,
        type: WIDGET_TYPES.DASHBOARD,
        alias: "wealth_consolidation_dashboard_0",
        subscriptions: {
          end_date_list: "wealth_year_picker",
          customer_list: "wealth_customer_selector"
        }
      }
    ],
    simulationsTab: [
      {
        page: simulationsPageID,
        type: WIDGET_TYPES.DASHBOARD,
        alias: "wealth_simulations_dashboard_0",
        subscriptions: {
          asset_list: "wealth_portfolio_selector"
        }
      }
    ]
  }
}

/**
 * Private Banking / Wealth static pages model
 */
export const models = {
  [PAGE_IDENTIFIER.overview]: {
    title: messages.synthesis,
    path: "/wealth/overview",
    cardContent: adminMessage.wealthSynthesisDescription,
    Icon: AssessmentIcon,
    // no selector here, it has been implemented in custom page
    renderSelector: () => null,
    component: withWidgets(SynthesisCustomTab, synthesisTabProps),
    TabContentDSL: pagesDSL.wealth.synthesisTab
  },
  [PAGE_IDENTIFIER.movements]: {
    title: messages.movement,
    path: "/wealth/movements",
    cardContent: adminMessage.wealthMovementsDescription,
    Icon: ListAltIcon,
    renderSelector: MonthsPortfoliosSelector,
    component: withWidgets(TemplateWealthPage, movementTabProps),
    TabContentDSL: pagesDSL.wealth.movementTab
  },
  [PAGE_IDENTIFIER.performance]: {
    title: messages.performance,
    path: "/wealth/performance",
    cardContent: adminMessage.wealthPerformanceDescription,
    Icon: TrendingUpIcon,
    renderSelector: YearPortfolioSelector,
    component: withWidgets(PerformanceCustomTab, performanceTabProps),
    TabContentDSL: pagesDSL.wealth.performanceTab
  },
  [PAGE_IDENTIFIER.composition]: {
    title: messages.composition,
    path: "/wealth/composition",
    cardContent: adminMessage.wealthCompositionDescription,
    Icon: AccountTreeIcon,
    renderSelector: DateWithPortfolios,
    component: withWidgets(TemplateWealthPage, compositionTabProps),
    TabContentDSL: pagesDSL.wealth.compositionTab
  },
  [PAGE_IDENTIFIER.consolidation]: {
    title: messages.consolidation,
    path: "/wealth/consolidation",
    cardContent: adminMessage.wealthConsolidationDescription,
    Icon: MergeTypeIcon,
    renderSelector: YearSelector,
    component: withWidgets(TemplateWealthPage, consolidationTabProps),
    TabContentDSL: pagesDSL.wealth.consolidationTab
  },
  [PAGE_IDENTIFIER.simulations]: {
    title: messages.simulations,
    path: "/wealth/simulations",
    cardContent: adminMessage.wealthSimulationsDescription,
    Icon: BarChartIcon,
    renderSelector: PortfoliosSelector,
    component: withWidgets(TemplateWealthPage, simulationsTabProps),
    TabContentDSL: pagesDSL.wealth.simulationsTab
  }
}

export default [
  synthesisPageID,
  movementPageID,
  performancePageID,
  compositionPageID,
  consolidationPageId,
  simulationsPageID
]
